import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Trash2 } from 'lucide-react';
import { graphql } from '@/gql';
import type { TabStatus } from '@/gql/graphql';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatCurrency, formatDateTime, formatShortId } from '@/lib/format';
import { TabLineItemRow, type TabLineItemRowData } from './TabLineItemRow';

const UpdateTabOrderItemDoc = graphql(/* GraphQL */ `
  mutation UpdateTabOrderItem($tabId: ID!, $orderItemId: ID!, $quantity: Int!) {
    updateTabOrderItem(tabId: $tabId, orderItemId: $orderItemId, quantity: $quantity) {
      id
      status
      canEditLineItems
      grandTotal
      amountPaid
      amountDue
      amountDiscount
      discount {
        id
        name
      }
      orders {
        id
        status
        grandTotal
        amountDue
        createdAt
        items {
          id
          productName
          quantity
          price
          lineTotal
        }
      }
    }
  }
`);

const RemoveOrderFromTabDoc = graphql(/* GraphQL */ `
  mutation RemoveOrderFromTab($tabId: ID!, $orderId: ID!) {
    removeOrderFromTab(tabId: $tabId, orderId: $orderId) {
      id
      status
      canEditLineItems
      grandTotal
      amountPaid
      amountDue
      amountDiscount
      orders {
        id
        status
        grandTotal
        amountDue
        createdAt
        items {
          id
          productName
          quantity
          price
          lineTotal
        }
      }
    }
  }
`);

const PARTIAL_PAID_HINT =
  'Paid amount cannot exceed the new total. Reduce quantities only when the new total stays at or above what is already paid.';

const OVERPAYMENT_ERROR_HINT =
  'Paid amount cannot exceed the new total. Reduce quantities only when the new total stays at or above what is already paid.';

const CLEAR_DISCOUNT_UUID = '00000000-0000-0000-0000-000000000000';

export interface TabLineItemsEditorOrder {
  id: string;
  status: string;
  createdAt?: string;
  items: TabLineItemRowData[];
}

interface TabLineItemsEditorProps {
  tabId: string;
  status: TabStatus | string;
  amountPaid: number;
  amountDue: number;
  orders: TabLineItemsEditorOrder[];
  onUpdated?: () => void;
}

function isCanceledStatus(status: string): boolean {
  return status.toUpperCase() === 'CANCELED' || status.toUpperCase() === 'CANCELLED' || status.toUpperCase() === 'LOSS';
}

/**
 * Editor for tab line items on open or partially paid tabs (current recognition day).
 * Groups products by order; supports qty edits and removing a whole order from the tab.
 */
export function TabLineItemsEditor({
  tabId,
  status,
  amountPaid,
  amountDue,
  orders,
  onUpdated,
}: TabLineItemsEditorProps) {
  const [updateTabOrderItem] = useMutation(UpdateTabOrderItemDoc);
  const [removeOrderFromTab] = useMutation(RemoveOrderFromTabDoc);
  const [pendingItemId, setPendingItemId] = useState<string | null>(null);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [orderPendingRemoval, setOrderPendingRemoval] = useState<TabLineItemsEditorOrder | null>(
    null,
  );
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [orderError, setOrderError] = useState<string | null>(null);

  const editableOrders = orders.filter((order) => !isCanceledStatus(order.status));
  const busy = pendingItemId !== null || pendingOrderId !== null;

  const orderHasItems = (order: TabLineItemsEditorOrder) =>
    order.items.some((i) => i.quantity > 0);

  const handleQuantityChange = useCallback(
    async (orderItemId: string, quantity: number) => {
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[orderItemId];
        return next;
      });
      setPendingItemId(orderItemId);

      try {
        await updateTabOrderItem({
          variables: { tabId, orderItemId, quantity },
        });
        onUpdated?.();
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Could not update this item.';
        const friendly = message.includes('Cannot reduce below amount already paid')
          || message.includes('cannot reduce below amount already paid')
          ? OVERPAYMENT_ERROR_HINT
          : message;
        setRowErrors((prev) => ({ ...prev, [orderItemId]: friendly }));
      } finally {
        setPendingItemId(null);
      }
    },
    [tabId, updateTabOrderItem, onUpdated],
  );

  const handleRemoveOrder = useCallback(async () => {
    if (!orderPendingRemoval) return;
    const orderId = orderPendingRemoval.id;
    setOrderError(null);
    setPendingOrderId(orderId);
    setOrderPendingRemoval(null);
    try {
      await removeOrderFromTab({ variables: { tabId, orderId } });
      onUpdated?.();
    } catch (e) {
      setOrderError(e instanceof Error ? e.message : 'Could not remove this order.');
    } finally {
      setPendingOrderId(null);
    }
  }, [orderPendingRemoval, removeOrderFromTab, tabId, onUpdated]);

  return (
    <Card title="Edit products">
      {String(status).toUpperCase() === 'PARTIAL_PAID' && (
        <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/50 dark:text-amber-100">
          {PARTIAL_PAID_HINT}
        </p>
      )}

      {orderError && (
        <p className="mb-3 text-sm text-red-600 dark:text-red-400" role="alert">
          {orderError}
        </p>
      )}

      {editableOrders.filter(orderHasItems).length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No products on this tab.</p>
      ) : (
        <div className="space-y-4">
          {editableOrders.map((order) => {
            const items = order.items.filter((i) => i.quantity > 0);
            if (items.length === 0) return null;
            const soleLineAtOne = items.length === 1 && items[0].quantity === 1;
            const canRemoveOrder = editableOrders.some(
              (o) => o.id !== order.id && orderHasItems(o),
            );

            return (
              <div
                key={order.id}
                className="rounded-xl border border-gray-200 p-3 dark:border-white/10"
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white">
                      Order #{formatShortId(order.id)}
                    </p>
                    {order.createdAt && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDateTime(order.createdAt)}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    leftIcon={<Trash2 className="h-4 w-4" aria-hidden />}
                    disabled={busy || !canRemoveOrder}
                    loading={pendingOrderId === order.id}
                    title={
                      !canRemoveOrder
                        ? 'Cannot remove the last order on the tab'
                        : 'Remove order from tab'
                    }
                    onClick={() => setOrderPendingRemoval(order)}
                  >
                    Remove order
                  </Button>
                </div>
                <ul className="space-y-2">
                  {items.map((item) => (
                    <TabLineItemRow
                      key={item.id}
                      item={item}
                      disabled={busy}
                      disableDecrease={soleLineAtOne && !canRemoveOrder}
                      loading={pendingItemId === item.id}
                      error={rowErrors[item.id] ?? null}
                      onQuantityChange={(quantity) => handleQuantityChange(item.id, quantity)}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <div className="sticky bottom-0 mt-4 flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-white/10 dark:bg-gray-800/80">
        <span className="text-gray-500 dark:text-gray-400">Paid</span>
        <span className="font-semibold text-gray-900 dark:text-white">
          {formatCurrency(amountPaid)}
        </span>
        <span className="text-gray-500 dark:text-gray-400">Due</span>
        <span className="font-semibold text-red-600 dark:text-red-400">
          {formatCurrency(amountDue)}
        </span>
      </div>

      <Modal
        open={orderPendingRemoval !== null}
        onClose={() => setOrderPendingRemoval(null)}
        title="Are you sure you want to remove this order?"
        description={
          orderPendingRemoval
            ? `Order #${formatShortId(orderPendingRemoval.id)} will be canceled and removed from this tab.`
            : undefined
        }
        size="sm"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setOrderPendingRemoval(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              leftIcon={<Trash2 className="h-4 w-4" aria-hidden />}
              onClick={() => {
                void handleRemoveOrder();
              }}
            >
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-300">
          The tab must keep at least one product on another order.
        </p>
      </Modal>
    </Card>
  );
}

export { CLEAR_DISCOUNT_UUID };
